import { format } from 'date-fns';
import { Heart, MessageSquare, Package, HelpCircle, type LucideIcon } from 'lucide-react';
import type { ActivityFeedItem } from './analyticsTypes';

// === FORMATTING UTILITIES ===

export const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateShort = (dateStr: string) => {
  const date = new Date(dateStr);
  return format(date, 'MMM d, h:mm a');
};

export const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
};

export const formatRelativeTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return format(date, 'MMM d');
};

// === GROUP BY DATE ===

export const groupActivityByDate = (
  items: ActivityFeedItem[]
): Record<string, ActivityFeedItem[]> => {
  const groups: Record<string, ActivityFeedItem[]> = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  items.forEach((item) => {
    const date = new Date(item.timestamp);
    let dateKey: string;

    if (date.toDateString() === today.toDateString()) {
      dateKey = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateKey = 'Yesterday';
    } else {
      dateKey = format(date, 'EEEE, MMMM d');
    }

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
  });

  return groups;
};

// === INQUIRY TAXONOMY (single source of truth for icon + label) ===
// Mirrors the priority used in get_buyer_activity dedup:
// requested_sample > requested_more_info > expressed_interest > sent_message
export interface InquiryMeta {
  icon: LucideIcon;
  label: string;       // short noun phrase for badges, e.g. "Requested sample"
  verbPhrase: string;  // sentence fragment used in the activity feed, e.g. "requested a sample of"
}

export const getInquiryMeta = (inquiryType?: string | null): InquiryMeta => {
  switch (inquiryType) {
    case 'requested_sample':
      return { icon: Package, label: 'Requested sample', verbPhrase: 'requested a sample of' };
    case 'requested_more_info':
      return { icon: HelpCircle, label: 'Requested info', verbPhrase: 'requested more info on' };
    case 'expressed_interest':
      return { icon: Heart, label: 'Expressed interest', verbPhrase: 'expressed interest in' };
    case 'sent_message':
      return { icon: MessageSquare, label: 'Sent message', verbPhrase: 'sent a message about' };
    default:
      return {
        icon: MessageSquare,
        label: (inquiryType ?? 'Inquiry').replace(/_/g, ' '),
        verbPhrase: 'inquired about',
      };
  }
};

